/* ------------------------ Functional imports start ------------------------ */
import type { AcceptType, CookieConsentConfig, CookieValue } from "vanilla-cookieconsent";
import Cookies from "js-cookie";
/* ------------------------- Functional imports end ------------------------- */


/* ------------------------- Utility functions start ------------------------ */
/**
 * Fetches the translation .json file for the specified language.
 * @param language - The language code for the translation file.
 */
async function lazyLoadTranslation (language: 'en' | 'de') {
  const res = await fetch(`locales/${language}/cookieConsent.json`)
  return await res.json()
}

function getCurrentLanguage() {
  // try to read current language from i18next cookie
  const lng_cookie: string | undefined =  Cookies.get('i18next')
  if(lng_cookie) return lng_cookie
  // try to read current language from url parameters
  const urlParams = new URLSearchParams(window.location.search)
  const lng_param: string | null = urlParams.get("lng")
  if(lng_param) return lng_param
  // return default language: english
  return 'en'
}

function updateConsentSignals(cookie: CookieValue) {
  const analyticsGranted = cookie.categories.includes('analytics')
  const nonPersonalizedAdsGranted = cookie.categories.includes('non_personalized_ads')
  const personalizedAdsGranted = cookie.categories.includes('personalized_ads')

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': analyticsGranted ? 'granted' : 'denied',
      'ad_storage': (nonPersonalizedAdsGranted || personalizedAdsGranted) ? 'granted' : 'denied',
      'ad_user_data': personalizedAdsGranted ? 'granted' : 'denied',
      'ad_personalization': personalizedAdsGranted ? 'granted' : 'denied',
      'functionality_storage': 'granted',
      'security_storage': 'granted'
    });
  }
}
/* -------------------------- Utility functions end ------------------------- */

const domain_url = window.location.hostname;
// get hostname without subdomain
let non_sub_domain = ""
const domain_parts = domain_url.split('.')
if(domain_parts.length >= 2) {
  non_sub_domain = domain_parts[domain_parts.length-2] + '.' + domain_parts[domain_parts.length-1]
}


const consentConfig: CookieConsentConfig = {
  // layout settings
  guiOptions: {
    consentModal: {
      layout: 'cloud',
      position: 'bottom center',
      equalWeightButtons: false,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  // callback functions
  onFirstConsent: ({cookie}: {cookie: CookieValue}) => {
    updateConsentSignals(cookie)
  },
  onConsent: ({cookie}: {cookie: CookieValue}) => {
    updateConsentSignals(cookie)
  },
  onChange: ({cookie}: {cookie: CookieValue}) => {
    updateConsentSignals(cookie)
  },

  // cc cookie settings
  cookie: {
    domain: (window.location.hostname.match('localhost') || !non_sub_domain)
      ? window.location.hostname
      : non_sub_domain,
    expiresAfterDays: (acceptType: AcceptType) => {
      return acceptType === 'all'
        ? 365.25
        : 7
    }
  },

  // tracking cookies settings
  autoClearCookies: true,
  categories: {
    necessary: {
      readOnly: true,
      enabled: true
    },
    analytics: {
      autoClear: {
        cookies: [
          {
            // google analytics
            name: /^(_ga|_gid)/,
          },
        ],
      },
    },
    non_personalized_ads: {
      autoClear: {
        cookies: [
          {
            // facebook pixel
            name: /^_fbp/
          },
          {
            // linked in
            name: /^(_lr | bcookie|li_gc|lidc|bscookie|UserMatchHistory)/
          }
        ]
      }
    },
    personalized_ads: {
      autoClear: {
        cookies: [
          {
            // facebook pixel
            name: /^_fbp/
          },
          {
            // linked in
            name: /^(_lr | bcookie|li_gc|lidc|bscookie|UserMatchHistory)/
          }
        ]
      }
    }
  },

  // localization settings
  language: {
    default: getCurrentLanguage(),
    // autoDetect: 'document',
    translations: {
      en: () => lazyLoadTranslation('en'),
      de: () => lazyLoadTranslation('de'),
    },
  },
};

export default consentConfig;
